import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
	GoogleMap,
	useJsApiLoader,
	MarkerClustererF,
	Marker,
	InfoWindowF,
} from "@react-google-maps/api";
import InfoWindowContent from "./infoWindowContent";
import OutsideClickHandler from "react-outside-click-handler";
const center = {
	lat: window.initialState.coords.lat,
	lng: window.initialState.coords.lng,
};
const clusterOptions = {
	textColor: "white",
	textSize: 14,
	fontWeight: "bold",
	url: `/assets/images/cluster.svg`,
	height: "48",
	width: "48",
	className: "gmap-marker",
};
const createKey = (obj) => {
	return obj.latitude + obj.longitude + obj.name;
};
const mapZoom = 16;
const styles = [
	{
		featureType: "poi.business",
		elementType: "all",
		stylers: [
			{
				visibility: "off",
			},
		],
	},
];

const GMap = ({
	callback,
	options,
	optionsLoading,
	toggleMap,
	isLoadedMap,
}) => {
	const global = useSelector((state) => state.global);
	const searchData = useSelector((state) => state.searchData);

	const { isLoaded } = useJsApiLoader({
		id: "google-map-script",
		googleMapsApiKey: global.settings.settings.googleMapsApiKey,
	});

	const [map, setMap] = useState(null);
	const [infoWindowOpen, setInfoWindowOpen] = useState(false);
	const [infoWindowInfo, setInfoWindowInfo] = useState(null);
	const [activeMarker, setActiveMarker] = useState(null);
	// rerender infowindow when data changes
	useEffect(() => {
		if (infoWindowInfo) {
			setInfoWindowOpen(true);
		}
	}, [infoWindowInfo]);

	const showInfoWindow = (obj, e, a) => {
		if (infoWindowOpen) {
			closeInfoWindow();
		}
		setActiveMarker(createKey(obj));
		setInfoWindowInfo(obj);
		// find facility in list and scroll to it
		callback(obj.facilityId);
	};
	const closeInfoWindow = () => {
		setInfoWindowOpen(false);
		setInfoWindowInfo(null);
		setActiveMarker(null);
		callback(null);
	};
	const onLoad = useCallback((map) => {
		setMap(map);
		map.setOptions({ styles: styles });
		// dealay list render, to avoid breaking rendering of google map - its bug from google
		setTimeout(() => {
			isLoadedMap(true);
		}, 100);
	}, []);
	const fitMapbyMarkersBounds = (optionsObj) => {
		const bounds = new window.google.maps.LatLngBounds();
		optionsObj.map((marker) => {
			return bounds.extend({
				lat: marker.latitude,
				lng: marker.longitude,
			});
		});
		map.fitBounds(bounds);
	};
	const setMapZoomLevel = (zoomLevel) => {
		let listener = window.google.maps.event.addListener(map, "idle", function() { 
			let zoom = map.getZoom();
			if (zoomLevel && zoom >= zoomLevel) {
				map.setZoom(zoomLevel);
			} 
			if(zoom >= 17) {
				map.setZoom(mapZoom);
			}
			window.google.maps.event.removeListener(listener); 
		  });
	};
	const isCityAndFacilityEmpty = (data) => {
		return (
			data.locationChecked &&
			data.city.value === ""
		);
	};
	useEffect(() => {
		if (map && options.length) {
			// center map based on pins available
			let zoomLevel = null;
			if (isCityAndFacilityEmpty(searchData)) {
				const optionsNearOneKm = options.filter((option) => {
					if (option.rawDistance.toFixed(0) <= 1999) {
						return option;
					}
					return false;
				});
				if(optionsNearOneKm.length === 0){ 
					const arr = [options[0]];
					fitMapbyMarkersBounds(arr);
					zoomLevel = mapZoom;
				} else {
					fitMapbyMarkersBounds(optionsNearOneKm);
					zoomLevel = mapZoom;
				}
			} else {
		
				fitMapbyMarkersBounds(options);
				zoomLevel = mapZoom;
			}
			setMapZoomLevel(zoomLevel);
		} else if (map) {
			map.setCenter(center);
		}
	}, [map, options]);

	const onUnmount = React.useCallback(function callback(map) {
		setMap(null);
	}, []);

	const onMapZoom = () => {
		if (map && map.zoom > 8) {
			closeInfoWindow();
		}
	};
	const outSideClick = () => {
		if (infoWindowOpen) {
			callback(null);
			closeInfoWindow();
		}
	};

	return isLoaded ? (
		<OutsideClickHandler onOutsideClick={outSideClick}>
			<GoogleMap
				mapContainerClassName="map"
				center={center}
				onClick={closeInfoWindow}
				zoom={8}
				onLoad={onLoad}
				onUnmount={onUnmount}
				onZoomChanged={onMapZoom}>
				{options?.length > 0 && (
					<MarkerClustererF styles={[clusterOptions]}>
						{(clusterer) =>
							options.map((location) => {
								return (
									<Marker
										key={createKey(location)}
										position={{
											lat: location.latitude,
											lng: location.longitude,
										}}
										icon={
											activeMarker === createKey(location)
												? "/assets/images/pin-active.svg"
												: "/assets/images/pin.svg"
										}
										onClick={(e) => showInfoWindow(location, e, this)}
										clusterer={clusterer}></Marker>
								);
							})
						}
					</MarkerClustererF>
				)}
				{infoWindowOpen ? (
					<InfoWindowF
						onCloseClick={closeInfoWindow}
						options={{ pixelOffset: new window.google.maps.Size(0, -60) }}
						position={{
							lat: infoWindowInfo.latitude,
							lng: infoWindowInfo.longitude,
						}}>
						<InfoWindowContent
							locationItem={infoWindowInfo}
							global={global}
							searchData={searchData}
						/>
					</InfoWindowF>
				) : null}
			</GoogleMap>
		</OutsideClickHandler>
	) : null;
};

export default GMap;
